// Turns listings from parseEtsyCsv (etsyCsv.js) into shop products.
//   * A listing already in the shop is matched by its Etsy listing number,
//     otherwise by SKU, and updated. Everything else becomes a new Draft.
//   * Photos (the first 6) are copied into Storage by the import-etsy-image
//     Supabase function, so the browser never downloads them itself.
//   * Every result comes back with plain-English notes for Andre.

import { supabase } from './supabaseClient';
import { MAX_PRODUCT_IMAGES, deleteProductImage } from './imageStore';

export var CATEGORIES = ['Briefcase', 'Tote', 'Messenger', 'Backpack', 'Wallet', 'Clutch', 'Belt', 'Other'];

// Checked in order; the first match wins. Wallet comes before Clutch so
// "Leather ID Wallet / Red wristlet" stays a Wallet.
var CATEGORY_WORDS = [
  ['Briefcase', /\b(briefcase|attach[eé]|laptop bag|portfolio)\b/i],
  ['Messenger', /\b(messenger|satchel|crossbody|cross-body)\b/i],
  ['Backpack', /\b(backpack|rucksack|knapsack)\b/i],
  ['Tote', /\b(tote|shopper)\b/i],
  ['Wallet', /\b(wallet|billfold|card holder|cardholder)\b/i],
  ['Clutch', /\b(clutch|purse|evening bag|wristlet|handbag)s?\b/i],
  ['Belt', /\bbelts?\b/i],
];

function matchWords(text) {
  // Etsy tags use underscores for spaces ("evening_bag").
  var t = String(text || '').replace(/_/g, ' ');
  for (var i = 0; i < CATEGORY_WORDS.length; i++) {
    if (CATEGORY_WORDS[i][1].test(t)) return CATEGORY_WORDS[i][0];
  }
  return null;
}

// The title decides; if it matches nothing, the listing's tags are tried.
export function guessCategory(name, tags) {
  return matchWords(name) || matchWords((tags || []).join(', ')) || 'Other';
}

var COLOUR_HEX = [
  ['dark brown', '#4A2E1A'], ['chocolate', '#4A2E1A'], ['espresso', '#3B2416'],
  ['light brown', '#A0703C'], ['cognac', '#9A4E1C'], ['chestnut', '#7B3F1D'],
  ['saddle', '#8B5A2B'], ['tan', '#C08A50'], ['camel', '#C19A6B'], ['honey', '#C68E3F'],
  ['brown', '#6B4226'], ['oxblood', '#4A0E0E'], ['burgundy', '#6D1A2A'], ['wine', '#6D1A2A'],
  ['red', '#8B2020'], ['black', '#1A1A1A'], ['navy', '#1F2A44'], ['blue', '#2E4A7A'],
  ['olive', '#5B5B2E'], ['green', '#1E3A2F'], ['grey', '#777777'], ['gray', '#777777'],
  ['white', '#F5F5F0'], ['cream', '#EDE3CC'], ['natural', '#D9B98C'], ['beige', '#D8C3A0'],
];

// A best guess at a swatch colour from its name. Andre can fix it in the form.
export function colourHex(name) {
  var n = String(name || '').toLowerCase();
  for (var i = 0; i < COLOUR_HEX.length; i++) {
    if (n.indexOf(COLOUR_HEX[i][0]) >= 0) return COLOUR_HEX[i][1];
  }
  return '#8B5A2B';
}

export function slugify(text) {
  return String(text || '').toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 80).replace(/-+$/, '') || 'product';
}

function uniqueSlug(name, products) {
  var base = slugify(name);
  var taken = {};
  products.forEach(function(p) { if (p.slug) taken[p.slug] = true; });
  if (!taken[base]) return base;
  for (var n = 2; ; n++) {
    if (!taken[base + '-' + n]) return base + '-' + n;
  }
}

// Which shop product (if any) this listing updates.
// -> { product, by: 'etsy' | 'sku' | null, error }
export function findMatch(listing, products) {
  if (listing.etsyListingId) {
    var byId = products.find(function(p) { return String(p.etsy_listing_id || '') === listing.etsyListingId; });
    if (byId) return { product: byId, by: 'etsy', error: null };
  }
  var sku = String(listing.sku || '').trim().toLowerCase();
  if (sku) {
    var bySku = products.filter(function(p) {
      if (String(p.sku || '').trim().toLowerCase() !== sku) return false;
      // Already tied to a different Etsy listing: not the same product.
      return !(listing.etsyListingId && p.etsy_listing_id && String(p.etsy_listing_id) !== listing.etsyListingId);
    });
    if (bySku.length > 1) {
      return { product: null, by: null, error: 'More than one shop product has the SKU "' + listing.sku + '", so it’s not clear which to update. Give them different SKUs, then import again.' };
    }
    if (bySku.length === 1) return { product: bySku[0], by: 'sku', error: null };
  }
  // Not a match (the plan matches only by Etsy number or SKU), but worth a
  // warning: the same title already in the shop would likely be a duplicate.
  var title = String(listing.name || '').trim().toLowerCase();
  var sameName = products.find(function(p) { return String(p.name || '').trim().toLowerCase() === title; }) || null;
  return { product: null, by: null, error: null, sameName: sameName };
}

function isColour(v) { return /colou?r/i.test(v.type + ' ' + v.name); }
function isSize(v) { return /size/i.test(v.type + ' ' + v.name); }

// Etsy variations -> the shop's colours and sizes.
// -> { colours: [] | null, variants: [] | null, notes: [] } (null = the listing has none)
export function mapVariations(listing, existing) {
  var notes = [];
  var colours = null;
  var variants = null;
  var stamp = Date.now();
  (listing.variations || []).forEach(function(v, vi) {
    var label = v.name || v.type || 'Option ' + (vi + 1);
    if (!v.values.length) return;
    if (isColour(v) && !colours) {
      var old = (existing && existing.colours) || [];
      colours = v.values.map(function(value, i) {
        var same = old.find(function(c) { return String(c.name).toLowerCase() === value.toLowerCase(); });
        return same
          ? Object.assign({}, same, { sort_order: i + 1 })
          : { id: stamp + vi * 100 + i, name: value, hex_code: colourHex(value), stock: 0, sort_order: i + 1 };
      });
      if (colours.some(function(c) { return !old.some(function(o) { return String(o.name).toLowerCase() === c.name.toLowerCase(); }); })) {
        notes.push('Colour swatches were guessed from their names; check them in the product form.');
      }
      // Colours only in the shop are kept, after Etsy's.
      old.forEach(function(o) {
        if (!colours.some(function(c) { return String(c.name).toLowerCase() === String(o.name).toLowerCase(); })) {
          colours.push(Object.assign({}, o, { sort_order: colours.length + 1 }));
        }
      });
    } else if (isSize(v) && !variants) {
      var oldSizes = (existing && existing.variants) || [];
      var priced = 0;
      variants = v.values.map(function(value, i) {
        var same = oldSizes.find(function(s) { return String(s.size_name).toLowerCase() === value.toLowerCase(); });
        if (same) return Object.assign({}, same, { sort_order: i + 1 });
        priced++;
        return { id: stamp + 1000 + vi * 100 + i, size_name: value, price: listing.price, sort_order: i + 1 };
      });
      // Sizes only in the shop are kept, with their own prices.
      oldSizes.forEach(function(s) {
        if (!variants.some(function(n) { return String(n.size_name).toLowerCase() === String(s.size_name).toLowerCase(); })) {
          variants.push(Object.assign({}, s, { sort_order: variants.length + 1 }));
        }
      });
      if (priced > 1) notes.push('New sizes were given the listing price ($' + listing.price + '); change any that cost more.');
    } else {
      notes.push('The Etsy option "' + label + '" (' + v.values.join(', ') + ') has no place in the shop, so it was left out.');
    }
  });
  return { colours: colours, variants: variants, notes: notes };
}

// Builds the product to save (without photos) and notes about it.
export function listingToProduct(listing, existing, products, sortOrder) {
  var notes = [];
  var mapped = mapVariations(listing, existing);
  notes = notes.concat(mapped.notes);

  var fields = {
    name: listing.name,
    base_price: listing.price,
    stock: listing.quantity,
  };
  // A blank on Etsy never wipes what Andre has typed in the shop.
  if (listing.description || !existing) fields.description = listing.description || '';
  if (listing.materials.length || !existing) fields.materials = listing.materials.join(', ');
  if (listing.tags.length || !existing) fields.tags = listing.tags;
  if (listing.sku || !existing) fields.sku = listing.sku || null;
  if (listing.etsyListingId) fields.etsy_listing_id = listing.etsyListingId;
  if (mapped.colours) fields.colours = mapped.colours;
  if (mapped.variants) fields.variants = mapped.variants;
  if (listing.currency !== 'USD') notes.push('The price was in ' + listing.currency + ' on Etsy; it was copied as $' + listing.price + '. Please check it.');

  if (existing) return { product: Object.assign({}, existing, fields), notes: notes };

  var category = guessCategory(listing.name, listing.tags);
  if (category === 'Other') notes.push('Category set to Other; pick the right one in the product form.');
  return {
    product: Object.assign({
      id: null,
      slug: uniqueSlug(listing.name, products),
      category: category,
      dimensions: '',
      compare_price: null,
      low_stock_threshold: 2,
      status: 'draft',
      sort_order: sortOrder,
      variants: [],
      colours: [],
      images: [],
      etsy_listing_id: null,
    }, fields),
    notes: notes,
  };
}

// Asks the import-etsy-image function to copy one Etsy photo into Storage.
export async function copyEtsyImage(imageUrl, productId) {
  if (!supabase) return { url: null, error: 'Not connected to the database.' };
  try {
    var res = await supabase.functions.invoke('import-etsy-image', { body: { imageUrl: imageUrl, productId: productId } });
    if (res.error) {
      var message = null;
      var ctx = res.error.context;
      if (ctx && typeof ctx.json === 'function') {
        try { var body = await ctx.json(); message = body && body.error; } catch (e) { /* not JSON */ }
      }
      if (!message && (res.error.name === 'FunctionsFetchError' || res.error.name === 'FunctionsRelayError')) {
        message = 'The photo copier couldn’t be reached. Check your internet connection, and that import-etsy-image is deployed.';
      }
      if (!message && ctx && ctx.status === 404) message = 'The photo copier (import-etsy-image) isn’t deployed yet.';
      return { url: null, error: message || 'Could not copy the photo from Etsy.' };
    }
    if (!res.data || typeof res.data.url !== 'string') return { url: null, error: 'Could not copy the photo from Etsy.' };
    return { url: res.data.url, error: null };
  } catch (e) {
    console.error('copyEtsyImage failed:', e);
    return { url: null, error: 'Could not copy the photo from Etsy.' };
  }
}

// Copies the first 6 photos in order. -> { urls, notes }
async function copyPhotos(listing, productId, copyImage) {
  var wanted = listing.imageUrls.slice(0, MAX_PRODUCT_IMAGES);
  var notes = [];
  if (listing.imageUrls.length > MAX_PRODUCT_IMAGES) {
    notes.push('Etsy has ' + listing.imageUrls.length + ' photos; the shop keeps 6, so the first 6 were copied.');
  }
  var results = await Promise.all(wanted.map(function(url) { return copyImage(url, productId); }));
  var urls = [];
  var failures = [];
  results.forEach(function(r, i) {
    if (r.url) urls.push(r.url); else failures.push({ n: i + 1, error: r.error });
  });
  if (failures.length) {
    var same = failures.every(function(f) { return f.error === failures[0].error; });
    if (same) {
      notes.push((failures.length === wanted.length ? 'No photos could be copied' : 'Photo' + (failures.length > 1 ? 's ' : ' ') + failures.map(function(f) { return f.n; }).join(', ') + ' could not be copied') + ': ' + failures[0].error);
    } else {
      failures.forEach(function(f) { notes.push('Photo ' + f.n + ' could not be copied: ' + f.error); });
    }
  }
  return { urls: urls, notes: notes };
}

// Imports the chosen listings one at a time.
// options: { products, saveProduct, copyImage?, deleteImage?, onProgress?(done, total, listing) }
// -> [{ listing, outcome: 'created' | 'updated' | 'failed', productName, error, notes }]
export async function importListings(listings, options) {
  var saveProduct = options.saveProduct;
  var copyImage = options.copyImage || copyEtsyImage;
  var deleteImage = options.deleteImage || deleteProductImage;
  var known = (options.products || []).slice();
  var nextSort = known.reduce(function(max, p) { return Math.max(max, Number(p.sort_order) || 0); }, 0) + 1;
  var results = [];

  for (var i = 0; i < listings.length; i++) {
    var listing = listings[i];
    if (options.onProgress) options.onProgress(i, listings.length, listing);
    var result = await importOne(listing);
    results.push(result);
  }
  if (options.onProgress) options.onProgress(listings.length, listings.length, null);
  return results;

  async function importOne(listing) {
    var match = findMatch(listing, known);
    if (match.error) return { listing: listing, outcome: 'failed', productName: null, error: match.error, notes: [] };
    var existing = match.product;
    var built = listingToProduct(listing, existing, known, nextSort);
    var notes = built.notes.slice();

    if (existing) {
      var photosToAdd = [];
      if (!(existing.images || []).length && listing.imageUrls.length) {
        var copiedForOld = await copyPhotos(listing, existing.id, copyImage);
        photosToAdd = copiedForOld.urls;
        notes = notes.concat(copiedForOld.notes);
      } else if ((existing.images || []).length) {
        notes.push('Kept its current photos in the shop.');
      }
      var updated = Object.assign({}, built.product, photosToAdd.length ? { images: photosToAdd } : {});
      var saved = await saveProduct(updated);
      if (saved.error) {
        for (var d = 0; d < photosToAdd.length; d++) await deleteImage(photosToAdd[d]);
        return { listing: listing, outcome: 'failed', productName: existing.name, error: saved.error, notes: notes };
      }
      replaceKnown(saved.data);
      notes.unshift(match.by === 'etsy' ? 'Matched by Etsy listing number.' : 'Matched by SKU "' + listing.sku + '".');
      return { listing: listing, outcome: 'updated', productName: existing.name, error: null, notes: notes };
    }

    // New: save first (to get its id for the photo folder), then add photos.
    var created = await saveProduct(built.product);
    if (created.error) return { listing: listing, outcome: 'failed', productName: null, error: created.error, notes: notes };
    nextSort++;
    var product = created.data;
    replaceKnown(product);
    if (!listing.imageUrls.length) {
      notes.push('The listing had no photos.');
    } else {
      var copied = await copyPhotos(listing, product.id, copyImage);
      notes = notes.concat(copied.notes);
      if (copied.urls.length) {
        var withPhotos = await saveProduct(Object.assign({}, product, { images: copied.urls }));
        if (withPhotos.error) {
          for (var k = 0; k < copied.urls.length; k++) await deleteImage(copied.urls[k]);
          notes.push('The product was created, but its photos couldn’t be saved: ' + withPhotos.error);
        } else {
          replaceKnown(withPhotos.data);
        }
      }
    }
    return { listing: listing, outcome: 'created', productName: product.name, error: null, notes: notes };
  }

  function replaceKnown(product) {
    if (!product) return;
    var at = known.findIndex(function(p) { return p.id === product.id; });
    if (at >= 0) known[at] = product; else known.push(product);
  }
}

// "3 imported (2 new drafts, 1 updated). 1 could not be imported."
export function summaryText(results) {
  var created = results.filter(function(r) { return r.outcome === 'created'; }).length;
  var updated = results.filter(function(r) { return r.outcome === 'updated'; }).length;
  var failed = results.length - created - updated;
  var parts = [];
  if (created + updated) {
    var bits = [];
    if (created) bits.push(created + ' new draft' + (created !== 1 ? 's' : ''));
    if (updated) bits.push(updated + ' updated');
    parts.push((created + updated) + ' imported (' + bits.join(', ') + ').');
  }
  if (failed) parts.push(failed + ' could not be imported.');
  return parts.join(' ') || 'Nothing was imported.';
}
